import { useState, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card } from '../../../components/ui';
import { useSessionMapChannel } from '../../../hooks/useSessionMapChannel';
import SessionMapGrid from '../../../components/teacher/SessionMapGrid';
import SessionMapLegend from '../../../components/teacher/SessionMapLegend';

function updateGridWithResponse(gridData, message) {
  return gridData.map(row => {
    if (row.student_id === message.student_id) {
      return {
        ...row,
        responses: {
          ...row.responses,
          [message.question_id]: message.response_data
        }
      };
    }
    return row;
  });
}

function updateStudentCurrentQuestion(students, message) {
  return students.map(student => {
    if (student.id === message.student_id) {
      return {
        ...student,
        current_question_id: message.question_id
      };
    }
    return student;
  });
}

function addNewStudent(gridData, students, message) {
  // Check if student already exists
  if (students.some(s => s.id === message.student_id)) {
    return { gridData, students };
  }

  const newStudent = {
    id: message.student_id,
    full_name: message.full_name,
    initials: message.initials,
    joined_at: message.timestamp,
    current_question_id: null
  };

  const newGridRow = {
    student_id: message.student_id,
    current_question_id: null,
    responses: {}
  };

  return {
    gridData: [...gridData, newGridRow],
    students: [...students, newStudent]
  };
}

export default function SessionMap({ session, activity, questions, students: initialStudents, grid_data: initialGridData }) {
  const [gridData, setGridData] = useState(initialGridData);
  const [students, setStudents] = useState(initialStudents);

  const handleMessage = useCallback((message) => {
    switch (message.type) {
      case 'response_submitted':
        setGridData(prev => updateGridWithResponse(prev, message));
        break;
      case 'current_question_changed':
        setStudents(prev => updateStudentCurrentQuestion(prev, message));
        break;
      case 'student_joined':
        const { gridData: newGridData, students: newStudents } = addNewStudent(gridData, students, message);
        setGridData(newGridData);
        setStudents(newStudents);
        break;
    }
  }, [gridData, students]);

  useSessionMapChannel(session.id, handleMessage);

  return (
    <TeacherShell maxWidth="wide" scrollTitle={<><Link href={`/teacher/sessions/${session.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{activity.title}</Link>{'  ›  Live Map'}</>}>
      <TeacherPageHeader
        title="Live Session Map"
        subtitle={activity.title}
        backLink={`/teacher/sessions/${session.id}`}
        backLabel="Back to session"
      />

      <div className="space-y-6">
        <SessionMapLegend />

        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <SessionMapGrid
              questions={questions}
              students={students}
              gridData={gridData}
            />
          </div>
        </Card>

        <div className="text-sm text-gray-500 dark:text-gray-400">
          {students.length} student{students.length !== 1 ? 's' : ''} connected
          {' \u2022 '}
          {questions.length} question{questions.length !== 1 ? 's' : ''}
        </div>
      </div>
    </TeacherShell>
  );
}
